import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { cn } from "../../lib/utils/utils";
import {
  createLocalId,
  DEFAULT_MAX_FILE_SIZE,
  formatMaximumSize,
  validateFile,
} from "../../lib/utils/fileUpload";
import { Label } from "../ui/label";
import { FileUploadRow } from "./FileUploadRow";

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const DEFAULT_LABELS = {
  noFileSelected: "No file selected",
  chooseFile: "Choose File",
  uploading: "Uploading...",
  uploadSuccess: "Successfully uploaded",
  preview: "Preview",
  replace: "Replace",
  remove: "Remove",
  retry: "Retry",
  upload: "Upload",
  deleting: "Removing...",
  pleaseWait: "Please wait until the upload finishes.",
};

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
};

const nameFromUrl = (url, fallback = "Current file") => {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(pathname.split("/").filter(Boolean).pop() || fallback);
  } catch {
    return fallback;
  }
};

const mimeFromName = (name) => {
  if (/\.pdf$/i.test(name)) return "application/pdf";
  if (/\.png$/i.test(name)) return "image/png";
  if (/\.jpe?g$/i.test(name)) return "image/jpeg";
  if (/\.gif$/i.test(name)) return "image/gif";
  if (/\.webp$/i.test(name)) return "image/webp";
  return "application/octet-stream";
};

const normalizeExistingFile = (file, index) => {
  if (typeof file === "string") {
    const name = nameFromUrl(file);
    return {
      localId: `existing-${index}-${file}`,
      serverId: `existing-${index}`,
      name,
      size: undefined,
      mimeType: mimeFromName(name),
      url: file,
      status: "success",
      progress: 100,
    };
  }

  const url = file?.url || file?.image_url || file?.pdf_url;
  const name = file?.name || file?.originalName || nameFromUrl(url, "Current file");
  return {
    localId: `existing-${file?.id || index}-${url || name}`,
    serverId: String(file?.id || `existing-${index}`),
    name,
    size: file?.size,
    mimeType: file?.mimeType || mimeFromName(name),
    url,
    status: "success",
    progress: 100,
    original: file,
  };
};

/**
 * Inline picker for legacy forms that submit their File objects with the
 * record's existing multipart request. It intentionally does not call the
 * standalone upload endpoint or mark local files as server-confirmed.
 */
const InlineFormFileUpload = ({
  id,
  label,
  optionalLabel,
  value,
  onChange,
  existingFiles = [],
  onRemoveExisting,
  onStageExistingReplacement,
  onCancelExistingReplacement,
  accept = IMAGE_TYPES,
  maxSizeBytes = DEFAULT_MAX_FILE_SIZE,
  maxFiles = 1,
  countExistingTowardsMax = true,
  disabled = false,
  required = false,
  helperText,
  error,
  labels: customLabels,
  className,
}) => {
  const generatedId = useId();
  const inputId = id || `inline-form-file-${generatedId.replace(/:/g, "")}`;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;
  const inputRef = useRef(null);
  const replacementTargetRef = useRef(null);
  const objectUrlsRef = useRef(new Map());
  const localIdsRef = useRef(new WeakMap());
  const replacementFilesRef = useRef(new WeakMap());
  const [previewVersion, setPreviewVersion] = useState(0);
  const [selectionError, setSelectionError] = useState("");
  const labels = {
    ...DEFAULT_LABELS,
    readyToSave: "Ready to upload when you save this form.",
    ...customLabels,
  };
  const resolvedHelperText = helperText || `Maximum ${formatMaximumSize(maxSizeBytes)}`;
  const normalizedMaxFiles = Math.max(1, Number(maxFiles) || 1);
  const localFiles = asArray(value);
  const remoteItems = useMemo(
    () => asArray(existingFiles).map(normalizeExistingFile),
    [existingFiles],
  );

  useEffect(() => {
    let changed = false;
    const activeFiles = new Set(localFiles);
    objectUrlsRef.current.forEach((url, file) => {
      if (!activeFiles.has(file)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(file);
        changed = true;
      }
    });
    localFiles.forEach((file) => {
      if (!objectUrlsRef.current.has(file)) {
        objectUrlsRef.current.set(file, URL.createObjectURL(file));
        changed = true;
      }
      if (!localIdsRef.current.has(file)) {
        localIdsRef.current.set(file, createLocalId());
      }
    });
    if (changed) setPreviewVersion((version) => version + 1);
  }, [value]);

  useEffect(() => () => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
  }, []);

  const localItems = localFiles.map((file) => {
    if (!localIdsRef.current.has(file)) {
      localIdsRef.current.set(file, createLocalId());
    }
    return {
      localId: localIdsRef.current.get(file),
      file,
      name: file.name,
      size: file.size,
      mimeType: file.type || "application/octet-stream",
      previewUrl: objectUrlsRef.current.get(file),
      replacementFor: replacementFilesRef.current.get(file),
      status: "selected",
      progress: 0,
    };
  });
  void previewVersion;

  const replacedRemoteIds = new Set(localItems.map((item) => item.replacementFor).filter(Boolean));
  const visibleRemoteItems = remoteItems.filter(
    (item) => !replacedRemoteIds.has(item.localId),
  );
  const occupiedCount =
    (countExistingTowardsMax ? remoteItems.length : 0) +
    localItems.filter((item) => !item.replacementFor).length;
  const isAtMaximum = occupiedCount >= normalizedMaxFiles;

  const emitValue = (files) => {
    onChange?.(normalizedMaxFiles === 1 ? files[0] || null : files);
  };

  const openPicker = (replacementFor = null) => {
    if (disabled || (!replacementFor && isAtMaximum)) return;
    replacementTargetRef.current = replacementFor;
    inputRef.current?.click();
  };

  const handleInputChange = (event) => {
    const selected = Array.from(event.target.files || []);
    const replacementFor = replacementTargetRef.current;
    replacementTargetRef.current = null;
    event.target.value = "";
    if (!selected.length || disabled) return;

    if (replacementFor && selected.length > 1) {
      setSelectionError("Select one replacement file at a time.");
      return;
    }
    const availableSlots = Math.max(0, normalizedMaxFiles - occupiedCount);
    if (!replacementFor && selected.length > availableSlots) {
      setSelectionError(`You can upload a maximum of ${normalizedMaxFiles} files.`);
      return;
    }

    const validationItems = localFiles.map((file) => ({ file }));
    for (const file of selected) {
      const validationMessage = validateFile(file, {
        accept,
        maxSizeBytes,
        items: validationItems,
      });
      if (validationMessage) {
        setSelectionError(validationMessage);
        return;
      }
      validationItems.push({ file });
    }

    setSelectionError("");
    const localReplacementTarget = replacementFor
      ? localFiles.find((file) => localIdsRef.current.get(file) === replacementFor)
      : null;

    if (localReplacementTarget) {
      const inheritedReplacement = replacementFilesRef.current.get(localReplacementTarget);
      if (inheritedReplacement) {
        replacementFilesRef.current.set(selected[0], inheritedReplacement);
      }
    } else if (replacementFor) {
      replacementFilesRef.current.set(selected[0], replacementFor);
      const remoteItem = remoteItems.find((item) => item.localId === replacementFor);
      if (remoteItem) {
        onStageExistingReplacement?.(remoteItem.original || remoteItem, selected[0]);
      }
    }

    const nextFiles = localReplacementTarget
      ? localFiles.map((file) =>
          file === localReplacementTarget ? selected[0] : file,
        )
      : normalizedMaxFiles === 1
        ? [selected[0]]
        : [...localFiles, ...selected];
    emitValue(nextFiles);
  };

  const removeLocal = (localId) => {
    const removedFile = localFiles.find(
      (file) => localIdsRef.current.get(file) === localId,
    );
    const replacementFor = removedFile
      ? replacementFilesRef.current.get(removedFile)
      : null;
    if (replacementFor) {
      const remoteItem = remoteItems.find((item) => item.localId === replacementFor);
      if (remoteItem) {
        onCancelExistingReplacement?.(remoteItem.original || remoteItem, removedFile);
      }
    }
    const nextFiles = localFiles.filter(
      (file) => localIdsRef.current.get(file) !== localId,
    );
    emitValue(nextFiles);
  };

  const previewItem = (localId) => {
    const item = [...remoteItems, ...localItems].find((candidate) => candidate.localId === localId);
    const url = item?.previewUrl || item?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleRemoveExisting = (localId) => {
    const item = remoteItems.find((candidate) => candidate.localId === localId);
    if (item && onRemoveExisting) onRemoveExisting(item.original || item);
  };

  const showSelector = normalizedMaxFiles > 1 || (!remoteItems.length && !localItems.length);
  const visibleError = error || selectionError;
  const describedBy = [resolvedHelperText ? helperId : null, visibleError ? errorId : null]
    .filter(Boolean)
    .join(" ") || undefined;

  return (
    <div className={cn("w-full space-y-2", className)} aria-disabled={disabled || undefined}>
      {label && (
        <Label
          htmlFor={inputId}
          className="text-sm text-gray-800"
          onClick={(event) => {
            if (disabled || isAtMaximum) event.preventDefault();
          }}
        >
          <span>{label}</span>
          {required && <span className="text-red-600" aria-hidden="true">*</span>}
          {optionalLabel && <span className="font-normal text-gray-500">{optionalLabel}</span>}
          {required && <span className="sr-only">required</span>}
        </Label>
      )}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className="sr-only"
        accept={accept.join(",")}
        multiple={normalizedMaxFiles > 1}
        disabled={disabled}
        aria-required={required}
        aria-describedby={describedBy}
        onChange={handleInputChange}
        tabIndex={-1}
      />

      {showSelector && (
        <div className={cn(
          "flex min-h-12 w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm transition-colors",
          "hover:border-gray-400 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30",
          (disabled || isAtMaximum) && "cursor-not-allowed bg-gray-100 text-gray-500 opacity-70",
        )}>
          <span className="min-w-0 flex-1 truncate text-gray-500">{labels.noFileSelected}</span>
          <button
            type="button"
            className="inline-flex min-h-9 shrink-0 items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => openPicker()}
            disabled={disabled || isAtMaximum}
          >
            <Upload aria-hidden="true" className="h-4 w-4" />
            {labels.chooseFile}
          </button>
        </div>
      )}

      {normalizedMaxFiles > 1 && (
        <p className="text-xs text-gray-500" aria-live="polite">
          {remoteItems.length} of {normalizedMaxFiles} files uploaded
          {localItems.length > 0
            ? ` · ${localItems.length} selected for this form`
            : ""}
        </p>
      )}

      {[...visibleRemoteItems, ...localItems].map((item) => (
        <FileUploadRow
          key={item.localId}
          item={item}
          disabled={disabled}
          labels={labels}
          canReplace={
            normalizedMaxFiles === 1 ||
            item.status === "selected" ||
            Boolean(onStageExistingReplacement)
          }
          canRemove={item.status === "selected" || Boolean(onRemoveExisting)}
          replacementInProgress={replacedRemoteIds.has(item.localId)}
          showUploadAction={false}
          onPreview={previewItem}
          onReplace={openPicker}
          onRetry={() => {}}
          onRemove={item.status === "selected" ? removeLocal : handleRemoveExisting}
          onUpload={() => {}}
        />
      ))}

      {visibleError && (
        <p id={errorId} className="text-xs font-medium text-red-700" role="alert">
          {visibleError}
        </p>
      )}
      {resolvedHelperText && (
        <p id={helperId} className="text-xs text-gray-500">
          {resolvedHelperText}
        </p>
      )}
    </div>
  );
};

export { IMAGE_TYPES, InlineFormFileUpload };
export default InlineFormFileUpload;
